import { ApiCollectionResponse } from 'app/api';
import { FILE_UPLOADS_URL } from 'app/api/file-upload';
import { prepareRequestData } from 'app/api/helpers/prepareRequestData';
import { prepareResponseData } from 'app/api/helpers/prepareResponseData';
import { getAccessToken } from 'app/services/jwtService';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { getErrorMessagesAsArray as oldGetErrorMessagesAsArray } from '../api/helpers/errorHandling';
import { API_URL } from '../core/config/index';
import { getErrorMessagesAsArray } from './errorHandling';

export interface RequestConfig {
  errorType: 'new' | 'legacy';
  removeResponseInterceptor?: boolean;
  removeRequestInterceptor?: boolean;
}

let instance: AxiosInstance;
let responseInterceptorId: number = 0;
let requestInterceptorId: number = 0;

export const client = (() => {
  const token = getAccessToken();

  instance = axios.create({
    baseURL: API_URL + '/v2/',
    // timeout: 1000,
    headers: {
      Accept: '*/*',
      'Content-type': 'application/json; charset=UTF-8',
      common: {
        Authorization: 'Bearer ' + token,
      },
    },
  });

  return instance;
})();

export const request = async function (
  options: AxiosRequestConfig,
  parseFn: (data: any) => void,
  requestConfig: RequestConfig = {
    errorType: 'new',
    removeResponseInterceptor: false,
    removeRequestInterceptor: false,
  },
) {
  const onSuccess = function (response: AxiosResponse) {
    const { data } = response;
    return parseFn ? parseFn(data) : data;
  };

  const onError = function (error: AxiosError) {
    return Promise.reject(
      requestConfig.errorType === 'new' ? getErrorMessagesAsArray(error) : oldGetErrorMessagesAsArray(error),
    );
  };

  // Add current JWT token to the request (for cases like login-as / logout and login as another user etc)
  const opts: AxiosRequestConfig = {
    ...options,
    headers: {
      ...options.headers,
      Authorization: 'Bearer ' + getAccessToken(),
    },
  };

  if (!requestConfig?.removeRequestInterceptor && requestInterceptorId === 0) {
    instance = registerRequestInterceptor(instance);
  } else {
    instance.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = 0;
  }

  if (!requestConfig?.removeResponseInterceptor && responseInterceptorId === 0) {
    instance = registerResponseInterceptor(instance);
  } else {
    instance.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = 0;
  }

  return client(opts).then(onSuccess).catch(onError);
};

export const getCollectionResponse = function <T>(data: any): ApiCollectionResponse<T> {
  return {
    results: data.models.results,
    totalEntries: data.models.totalEntries || data.models.total_entries,
    totalPages: data.models.totalPages || data.models.total_pages,
    hasNextPage: data.models.hasNextPage || data.models.has_next_page,
    hasPreviousPage: data.models.hasPreviousPage || data.models.has_previous_page,
  };
};

function registerRequestInterceptor(instance: AxiosInstance): AxiosInstance {
  requestInterceptorId = instance.interceptors.request.use(
    function (config) {
      return {
        ...config,
        data: prepareRequestData(config.data),
        params: prepareRequestData(config.params),
      };
    },
    function (error) {
      return Promise.reject(error);
    },
  );

  return instance;
}

function registerResponseInterceptor(instance: AxiosInstance): AxiosInstance {
  responseInterceptorId = instance.interceptors.response.use(
    function (response) {
      // exceptions - presign url to AwsS3 upload
      if (response.config.url === FILE_UPLOADS_URL) {
        return response;
      }

      return { ...response, data: prepareResponseData(response.data) };
    },
    (err) => {
      return Promise.reject(err);
    },
  );
  return instance;
}

export const REQUEST_DELAY_MS = parseInt(localStorage.getItem('response-timeout') || '1', 10);

/**
 * Generic delay wrapper for any async function
 * @param fn The async function to wrap
 * @param delayMs Optional delay in milliseconds (default: 5000ms)
 * @returns A wrapped version of the function that delays before execution
 *
 * @example
 * const delayedApiCall = withDelay(apiGetTickets, 5000);
 * delayedApiCall(params, signal);
 */
export function withDelay<TArgs extends any[], TReturn>(
  fn: (...args: TArgs) => Promise<TReturn>,
  delayMs: number = REQUEST_DELAY_MS,
): (...args: TArgs) => Promise<TReturn> {
  return (...args: TArgs): Promise<TReturn> => {
    // Check if last argument is an AbortSignal
    const lastArg = args[args.length - 1];
    const signal = lastArg instanceof AbortSignal ? lastArg : undefined;

    return new Promise((resolve, reject) => {
      // Check if already aborted
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }

      const timeoutId = setTimeout(() => {
        fn(...args)
          .then(resolve)
          .catch(reject);
      }, delayMs);

      // Handle abort signal
      if (signal) {
        signal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
          reject(new DOMException('Aborted', 'AbortError'));
        });
      }
    });
  };
}
